/**
 * scoreboardModel — Pure mid-match scoreboard aggregation (SPY-41 / FID-HUD-03).
 * No DOM / Three.js. Rows: team, champion, level, KDA, CS, gold.
 */

export const SCOREBOARD_SORT_KEYS = Object.freeze(['team', 'gold', 'kda']);

function asInt(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : fallback;
}

export function formatKda(kills, deaths, assists) {
  return `${asInt(kills)}/${asInt(deaths)}/${asInt(assists)}`;
}

function teamRank(team) {
  if (team === 'blue') return 0;
  if (team === 'red') return 1;
  return 2;
}

function kdaScore(row) {
  return asInt(row.kills) * 10000 + asInt(row.assists) * 100 - asInt(row.deaths);
}

/**
 * @param {object[]} rows
 * @param {string} [sortKey]
 * @returns {object[]}
 */
export function sortScoreboardRows(rows, sortKey = 'team') {
  const list = Array.isArray(rows) ? rows.slice() : [];
  const key = SCOREBOARD_SORT_KEYS.includes(sortKey) ? sortKey : 'team';
  if (key === 'gold') {
    list.sort((a, b) => b.gold - a.gold || teamRank(a.team) - teamRank(b.team));
    return list;
  }
  if (key === 'kda') {
    list.sort((a, b) => kdaScore(b) - kdaScore(a) || b.gold - a.gold);
    return list;
  }
  list.sort((a, b) => {
    const t = teamRank(a.team) - teamRank(b.team);
    if (t !== 0) return t;
    return b.gold - a.gold;
  });
  return list;
}

function toRow(entity) {
  const kills = asInt(entity.kills ?? entity.stats?.kills);
  const deaths = asInt(entity.deaths ?? entity.stats?.deaths);
  const assists = asInt(entity.assists ?? entity.stats?.assists);
  const gold = asInt(entity.gold ?? entity.stats?.gold);
  const cs = asInt(entity.cs ?? entity.stats?.cs);
  const level = asInt(entity.level ?? entity.stats?.level, 1) || 1;
  const champion = String(entity.name || entity.champKey || entity.id || '?');
  return {
    id: entity.id || champion,
    team: entity.team || 'neutral',
    champion,
    level,
    kills,
    deaths,
    assists,
    kda: formatKda(kills, deaths, assists),
    cs,
    gold
  };
}

/**
 * @param {object[]|null|undefined} entities
 * @returns {object[]}
 */
export function buildScoreboardRows(entities, sortKey = 'team') {
  if (!Array.isArray(entities)) return [];
  const rows = entities
    .filter((e) => e && e.type === 'champion')
    .map(toRow);
  return sortScoreboardRows(rows, sortKey);
}
